import type { LanguageChoice } from '../../../i18n/languages';
import type { MePayload } from '../../../access/api/schemas';

export const IDENTIFIER_KINDS = ['PHONE', 'EMAIL'] as const;
export type IdentifierKind = (typeof IDENTIFIER_KINDS)[number];

export const PASSCODE_CHANNELS = ['WHATSAPP', 'SMS', 'EMAIL'] as const;
export type PasscodeChannel = (typeof PASSCODE_CHANNELS)[number];

export const ALERT_CHANNELS = ['WHATSAPP', 'SMS'] as const;
export type AlertChannel = (typeof ALERT_CHANNELS)[number];

export const CODE_PURPOSES = ['LOGIN', 'PASSWORD_RESET'] as const;
export type CodePurpose = (typeof CODE_PURPOSES)[number];

export const PASSCODE_REFUSALS = [
  'UNKNOWN_IDENTIFIER',
  'LOCKED',
  'CODE_EXPIRED',
  'WRONG_CODE',
  'TOO_MANY_SENDS',
  'RESEND_TOO_SOON',
  'NO_CODE_REQUESTED',
  'CHANNEL_UNAVAILABLE',
  'WRONG_PURPOSE',
  'NO_VERIFIED_PHONE',
  'LINK_EXPIRED',
  'NO_PASSWORD_SET',
  'USE_CODE',
  'WRONG_PASSWORD',
  'TOO_SHORT',
  'MISMATCH',
  'GRANT_REQUIRED',
  'GRANT_EXPIRED',
  'GRANT_INVALID',
  'PASSWORD_ALREADY_SET',
  'NO_VERIFIED_CONTACT',
  'PHONE_TAKEN',
  'EMAIL_TAKEN',
  'INVALID_EMAIL',
  'INVALID_PHONE',
  'SEND_FAILED',
  'PASSWORD_TOO_SHORT',
] as const;
export type PasscodeRefusalCode = (typeof PASSCODE_REFUSALS)[number];

export interface CodeSent {
  readonly sentOn: PasscodeChannel;
  readonly maskedTo: string;
  readonly expiresInSeconds: number;
  readonly resendInSeconds: number;
  readonly verifiesIdentifier: boolean;
  readonly codeLength: number;
}

export interface PhoneCodeSent extends CodeSent {
  readonly sentOn: AlertChannel;
}

export interface EmailCodeSent extends CodeSent {
  readonly sentOn: 'EMAIL';
}

export interface SignedInUser {
  readonly userId: string;
  readonly userName: string;
  readonly email: string;
  readonly isStaff: boolean;
  readonly roleKey: string | null;
  readonly adminGrant: string | null;
}

export interface SideBarItem {
  readonly name: string;
  readonly link?: string;
  readonly children?: readonly SideBarItem[];
}

export interface SessionLayout {
  readonly sideBar: readonly SideBarItem[];
  readonly landingPage: string;
}

export interface PasswordGrant {
  readonly token: string;
  readonly expiresInSeconds: number;
}

export interface OpenedSession extends SignedInUser {
  readonly layout: SessionLayout | null;
  readonly passwordGrant: PasswordGrant | null;
  readonly access: MePayload | null;
  readonly language: LanguageChoice | null;
}

export interface IdentifyResult {
  readonly kind: IdentifierKind;
  readonly masked: string;
  readonly channels: readonly PasscodeChannel[];
  readonly passwordSet: boolean;
  readonly phoneVerified: boolean;
  readonly emailVerified: boolean;
}

export interface PhoneVerified {
  readonly number: string;
  readonly isPhoneNumberVerified: true;
  readonly realtimeChannel: AlertChannel;
  readonly moved: boolean;
}

export interface EmailVerified {
  readonly email: string;
  readonly isEmailAddressVerified: true;
}
